import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { AuthProvider } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { ThemeModeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { AppSettingsProvider } from './contexts/AppSettingsContext';
import { useTheme } from './hooks/useTheme';
import { useAuth } from './hooks/useAuth';

import KioskDashboard from './components/tablet/KioskDashboard';
import FamilyLoginPage from './components/auth/FamilyLoginPage';
import RoleSelectPage from './components/auth/RoleSelectPage';
import OnboardingTutorial from './components/auth/OnboardingTutorial';
import SetupWizard from './components/auth/SetupWizard';
import AppLayout from './components/shared/AppLayout';

const Hallinta = lazy(() => import('./components/mobile/Hallinta'));
const TaskManager = lazy(() => import('./components/mobile/TaskManager'));
const ChildProgress = lazy(() => import('./components/mobile/ChildProgress'));
const Settings = lazy(() => import('./components/mobile/Settings'));
const CalendarPage = lazy(() => import('./components/shared/CalendarPage'));
const ShopPage = lazy(() => import('./components/shop/ShopPage'));
const NewsPage = lazy(() => import('./components/shop/NewsPage'));
const ProfiiliPage = lazy(() => import('./components/shared/ProfiiliPage'));
const WikiPage = lazy(() => import('./components/mobile/WikiPage'));

const LazyFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

function AppContent() {
  const { user, loading, canAccess, isKioskContext, deviceAuthenticated, deviceLoading, verifyDevice, needsSetup, setNeedsSetup } = useAuth();
  const theme = useTheme();
  const authTheme = createTheme({ palette: { primary: { main: '#e91e8c' }, mode: 'light' } });
  const [demoHints, setDemoHints] = useState(null);
  const [deviceOnboarded, setDeviceOnboarded] = useState(() => !!localStorage.getItem('device_onboarded'));
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialUserType, setTutorialUserType] = useState('child');

  useEffect(() => {
    fetch('/manifest.json').then(r => r.json()).then(m => {
      if (m.short_name || m.name) document.title = m.short_name || m.name;
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (deviceAuthenticated) {
      import('./services/api').then(({ default: api }) => {
        api.getSetupStatus().then(r => {
          if (r.data?.demoHints) setDemoHints(r.data.demoHints);
        }).catch(() => {});
      });
    } else if (!localStorage.getItem('device_onboarded')) {
      // familyLogout removes device_onboarded from localStorage — sync React state so RoleSelectPage shows after re-login
      setDeviceOnboarded(false);
    }
  }, [deviceAuthenticated]);

  const handleFirstLogin = (loggedInUser) => {
    setTutorialUserType(loggedInUser?.type === 'parent' ? 'parent' : 'child');
    setShowTutorial(true);
  };

  const handleOnboardingComplete = () => {
    localStorage.setItem('device_onboarded', '1');
    window.history.replaceState({}, '', '/');
    setDeviceOnboarded(true);
    setShowTutorial(false);
  };

  // Show loading while checking device auth
  if (deviceLoading) {
    return null;
  }

  // Show setup wizard for fresh install
  if (needsSetup) {
    return (
      <ThemeProvider theme={authTheme}>
        <CssBaseline />
        <SetupWizard onComplete={() => {
          setNeedsSetup(false);
          window.location.reload();
        }} />
      </ThemeProvider>
    );
  }

  // Show family login if device not authenticated
  if (!deviceAuthenticated) {
    return (
      <ThemeProvider theme={authTheme}>
        <CssBaseline />
        <FamilyLoginPage
          onSuccess={verifyDevice}
          onSetupRequired={() => setNeedsSetup(true)}
        />
      </ThemeProvider>
    );
  }

  if (loading) {
    return null;
  }

  // First-time device: show onboarding tutorial after login
  if (!deviceOnboarded && showTutorial) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <OnboardingTutorial
          userType={tutorialUserType}
          onComplete={handleOnboardingComplete}
          demoMode={!!demoHints}
        />
      </ThemeProvider>
    );
  }

  // Device is authenticated but hasn't completed first-time setup — show role selection
  if (!deviceOnboarded) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RoleSelectPage demoHints={demoHints} onFirstLogin={handleFirstLogin} />
      </ThemeProvider>
    );
  }

  // Device onboarded but no user logged in — show login (skip tutorial)
  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RoleSelectPage demoHints={demoHints} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <WebSocketProvider>
        <Router>
          <AppLayout>
            <Suspense fallback={<LazyFallback />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<KioskDashboard />} />
              <Route path="/kalenteri" element={<CalendarPage />} />
              <Route path="/kauppa" element={<ShopPage />} />
              <Route path="/uutiset" element={<NewsPage />} />
              <Route path="/profiili" element={<ProfiiliPage />} />

              {/* Legacy login routes - redirect to home (login is now popup) */}
              <Route path="/child-login" element={<Navigate to="/" replace />} />
              <Route path="/login" element={user ? <Navigate to="/hallinta" replace /> : <Navigate to="/" replace />} />

              {/* Protected routes — dynamic permissions */}
              <Route path="/hallinta" element={canAccess('/hallinta') ? <Hallinta /> : <Navigate to="/" replace />} />
              <Route path="/parent" element={<Navigate to="/hallinta" replace />} />
              <Route path="/tehtavat" element={canAccess('/tehtavat') ? <TaskManager /> : <Navigate to="/" replace />} />
              <Route path="/edistyminen/:userId" element={canAccess('/edistyminen') ? <ChildProgress /> : <Navigate to="/" replace />} />
              <Route path="/asetukset" element={(user?.type === 'parent' || user?.type === 'child' || isKioskContext) ? <Settings /> : <Navigate to="/" replace />} />
              <Route path="/wiki" element={canAccess('/hallinta') ? <WikiPage /> : <Navigate to="/" replace />} />
              <Route path="/hall-of-fame" element={<Navigate to="/" replace />} />
              <Route path="/admin/economy" element={<Navigate to="/hallinta" replace />} />
              <Route path="/profiili/cosmetics" element={<Navigate to="/profiili" replace />} />

              {/* Dev: preview onboarding */}
              <Route path="/onboarding" element={<OnboardingTutorial userType="child" onComplete={() => window.history.back()} />} />
              <Route path="/onboarding/parent" element={<OnboardingTutorial userType="parent" onComplete={() => window.history.back()} />} />
              <Route path="/setup" element={<SetupWizard onComplete={() => window.history.back()} />} />
              <Route path="/roleselect" element={<RoleSelectPage onFirstLogin={() => window.history.back()} />} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </AppLayout>
        </Router>
      </WebSocketProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ThemeModeProvider>
      <AppSettingsProvider>
        <AuthProvider>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </AuthProvider>
      </AppSettingsProvider>
    </ThemeModeProvider>
  );
}

export default App;
